import React, { useState, useEffect } from 'react';
import './Promotions.css';
import ConfirmDialog from '../common/ConfirmDialog';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const Promotions = () => {
  const [promotions, setPromotions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, promoId: null });
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [selectedPromotions, setSelectedPromotions] = useState([]);
  const [availableBrands, setAvailableBrands] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    image_url: '',
    image_secondary: '',
    link_url: '',
    cta_text: '',
    primary_cta: '',
    secondary_cta: '',
    badge: '',
    brand: '',
    background_theme: 'default',
    is_active: true,
    priority: 0,
    display_order: 0,
    start_at: '',
    end_at: ''
  });
  const [uploadingPrimary, setUploadingPrimary] = useState(false);
  const [uploadingBg, setUploadingBg] = useState(false);
  const [primaryPreview, setPrimaryPreview] = useState('');
  const [bgPreview, setBgPreview] = useState('');

  // Predefined background themes
  const backgroundThemes = {
    default: {
      name: 'Default (Ivory)',
      gradient: 'linear-gradient(135deg, #FDFCFB 0%, #FFFFFF 50%, #F8F7F5 100%)'
    },
    warm: {
      name: 'Warm Beige',
      gradient: 'linear-gradient(135deg, #FFF8F0 0%, #FFFFFF 50%, #FFF5EB 100%)'
    },
    cool: {
      name: 'Cool Gray',
      gradient: 'linear-gradient(135deg, #F8F9FA 0%, #FFFFFF 50%, #F5F6F7 100%)'
    },
    blush: {
      name: 'Soft Blush',
      gradient: 'linear-gradient(135deg, #FFF5F7 0%, #FFFFFF 50%, #FFF0F3 100%)'
    },
    mint: {
      name: 'Subtle Mint',
      gradient: 'linear-gradient(135deg, #F5FFF8 0%, #FFFFFF 50%, #F0FFF5 100%)'
    }
  };

  useEffect(() => {
    loadPromotions();
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      const response = await fetch(`${API_URL}/brands`);
      const data = await response.json();
      if (data.success && data.data) {
        setAvailableBrands(data.data);
      }
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const loadPromotions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/promotions?t=${Date.now()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Check for authentication errors
      if (response.status === 401) {
        console.error('Unauthorized - please log in again');
        setPromotions([]);
        // Optionally redirect to login
        // window.location.href = '/login';
        return;
      }
      
      const data = await response.json();
      console.log('Loaded promotions:', data);
      
      // Handle error responses
      if (data.success === false || data.error) {
        console.error('API error:', data.error);
        setPromotions([]);
        return;
      }
      
      // Handle both array response and object with data property
      if (Array.isArray(data)) {
        setPromotions(data);
      } else if (data.data && Array.isArray(data.data)) {
        setPromotions(data.data);
      } else {
        setPromotions([]);
      }
    } catch (error) {
      console.error('Error loading promotions:', error);
      setPromotions([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const url = editingPromo
        ? `${API_URL}/promotions/${editingPromo.id}`
        : `${API_URL}/promotions`;
      
      const method = editingPromo ? 'PUT' : 'POST';
      
      // Convert theme to gradient and remove background_theme (not a DB column)
      const { background_theme, ...dataWithoutTheme } = formData;
      
      // Clean up empty strings - convert to null for optional fields
      const submitData = {
        title: dataWithoutTheme.title,
        subtitle: dataWithoutTheme.subtitle || null,
        image_url: dataWithoutTheme.image_url,
        image_secondary: dataWithoutTheme.image_secondary || null,
        link_url: dataWithoutTheme.link_url || null,
        cta_text: dataWithoutTheme.cta_text || null,
        primary_cta: dataWithoutTheme.primary_cta || null,
        secondary_cta: dataWithoutTheme.secondary_cta || null,
        badge: dataWithoutTheme.badge || null,
        brand: dataWithoutTheme.brand || null,
        background_gradient: backgroundThemes[background_theme]?.gradient || backgroundThemes.default.gradient,
        is_active: dataWithoutTheme.is_active,
        priority: dataWithoutTheme.priority,
        display_order: dataWithoutTheme.display_order,
        start_at: dataWithoutTheme.start_at || null,
        end_at: dataWithoutTheme.end_at || null
      };
      
      console.log('Submitting promotion:', { method, url, data: submitData });
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });

      console.log('Response status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        console.log('Success:', result);
        await loadPromotions();
        closeModal();
      } else {
        const error = await response.json();
        console.error('Error response:', error);
        alert(`Failed to ${editingPromo ? 'update' : 'create'} promotion: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error saving promotion:', error);
      alert('Failed to save promotion. Check console for details.');
    }
  };

  const handleDelete = async (id) => {
    setConfirmDialog({ isOpen: true, promoId: id });
  };

  const handleConfirmDelete = async () => {
    const id = confirmDialog.promoId;
    setConfirmDialog({ isOpen: false, promoId: null });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/promotions/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        loadPromotions();
      }
    } catch (error) {
      console.error('Error deleting promotion:', error);
    }
  };

  const handleCancelDelete = () => {
    setConfirmDialog({ isOpen: false, promoId: null });
  };

  const toggleSelectPromotion = (id) => {
    setSelectedPromotions(prev =>
      prev.includes(id) ? prev.filter(pid => pid !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedPromotions.length === promotions.length) {
      setSelectedPromotions([]);
    } else {
      setSelectedPromotions(promotions.map(p => p.id));
    }
  };

  const handleBulkDelete = () => {
    if (selectedPromotions.length === 0) return;
    setBulkDeleteConfirm(true);
  };

  const confirmBulkDelete = async () => {
    setBulkDeleteConfirm(false);
    
    try {
      const token = localStorage.getItem('token');
      
      // Delete promotions one by one
      const deletePromises = selectedPromotions.map(id =>
        fetch(`${API_URL}/promotions/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        })
      );

      await Promise.all(deletePromises);
      
      // Refresh promotions list
      loadPromotions();
      setSelectedPromotions([]);
    } catch (error) {
      console.error('Error bulk deleting:', error);
    }
  };

  const cancelBulkDelete = () => {
    setBulkDeleteConfirm(false);
  };

  const handleToggleStatus = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/promotions/${id}/toggle`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        loadPromotions();
      }
    } catch (error) {
      console.error('Error toggling status:', error);
    }
  };

  const handleDragStart = (e, promo) => {
    setDraggedItem(promo);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetPromo) => {
    e.preventDefault();
    
    if (!draggedItem || draggedItem.id === targetPromo.id) {
      setDraggedItem(null);
      return;
    }

    // Swap priorities
    const draggedPriority = draggedItem.priority;
    const targetPriority = targetPromo.priority;

    try {
      const token = localStorage.getItem('token');
      
      // Update both priorities
      await Promise.all([
        fetch(`${API_URL}/promotions/${draggedItem.id}/priority`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ priority: targetPriority })
        }),
        fetch(`${API_URL}/promotions/${targetPromo.id}/priority`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ priority: draggedPriority })
        })
      ]);

      loadPromotions();
    } catch (error) {
      console.error('Error reordering promotions:', error);
    }

    setDraggedItem(null);
  };

  const openModal = (promo = null) => {
    if (promo) {
      setEditingPromo(promo);
      
      // Find matching theme from background_gradient
      let matchingTheme = 'default';
      if (promo.background_gradient) {
        const themeEntry = Object.entries(backgroundThemes).find(
          ([_, theme]) => theme.gradient === promo.background_gradient
        );
        if (themeEntry) {
          matchingTheme = themeEntry[0];
        }
      }
      
      setFormData({
        title: promo.title,
        subtitle: promo.subtitle || '',
        image_url: promo.image_url,
        image_secondary: promo.image_secondary || '',
        link_url: promo.link_url || '',
        cta_text: promo.cta_text || '',
        primary_cta: promo.primary_cta || '',
        secondary_cta: promo.secondary_cta || '',
        badge: promo.badge || '',
        brand: promo.brand || '',
        background_theme: matchingTheme,
        is_active: promo.is_active,
        priority: promo.priority,
        display_order: promo.display_order || 0,
        start_at: promo.start_at ? new Date(promo.start_at).toISOString().slice(0, 16) : '',
        end_at: promo.end_at ? new Date(promo.end_at).toISOString().slice(0, 16) : ''
      });
      setPrimaryPreview(promo.image_url);
      setBgPreview(promo.image_secondary || '');
    } else {
      setEditingPromo(null);
      setFormData({
        title: '',
        subtitle: '',
        image_url: '',
        image_secondary: '',
        link_url: '',
        cta_text: '',
        primary_cta: '',
        secondary_cta: '',
        badge: '',
        brand: '',
        background_theme: 'default',
        is_active: true,
        priority: 0,
        display_order: 0,
        start_at: '',
        end_at: ''
      });
      setPrimaryPreview('');
      setBgPreview('');
    }
    setShowModal(true);
  };

  const handleImageUpload = async (file, isPrimary = true) => {
    if (!file) return;

    const uploadFormData = new FormData();
    uploadFormData.append('image', file);

    try {
      if (isPrimary) setUploadingPrimary(true);
      else setUploadingBg(true);

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/upload/promotions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: uploadFormData
      });

      const data = await response.json();

      if (response.ok) {
        const imageUrl = `${API_URL.replace('/api', '')}${data.imageUrl}`;
        if (isPrimary) {
          setFormData(prev => ({ ...prev, image_url: imageUrl }));
          setPrimaryPreview(imageUrl);
        } else {
          setFormData(prev => ({ ...prev, image_secondary: imageUrl }));
          setBgPreview(imageUrl);
        }
      } else {
        alert(data.error || 'Failed to upload image');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image');
    } finally {
      if (isPrimary) setUploadingPrimary(false);
      else setUploadingBg(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPromo(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="promotions-management">
      <div className="promotions-header">
        <h2>Hero Promotions</h2>
        <button className="btn-add-promo" onClick={() => openModal()}>
          + Add Promotion
        </button>
      </div>

      {selectedPromotions.length > 0 && (
        <div className="bulk-actions-promo">
          <span>{selectedPromotions.length} selected</span>
          <button onClick={handleBulkDelete} className="btn-bulk-delete-promo">
            Delete Selected
          </button>
        </div>
      )}

      <div className="promotions-list">
        {promotions.length === 0 ? (
          <div className="no-promotions">
            <p>No promotions yet. Create your first promotion to display on the homepage hero.</p>
          </div>
        ) : (
          <table className="promotions-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selectedPromotions.length === promotions.length && promotions.length > 0}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th>Preview</th>
                <th>Title</th>
                <th>Brand</th>
                <th>Order</th>
                <th>Schedule</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {promotions.map((promo) => (
                <tr 
                  key={promo.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, promo)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, promo)}
                  className={draggedItem?.id === promo.id ? 'dragging' : ''}
                  style={{ cursor: 'move' }}
                >
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedPromotions.includes(promo.id)}
                      onChange={() => toggleSelectPromotion(promo.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td>
                    <div className="promo-preview">
                      <img src={promo.image_url} alt={promo.title} />
                      {promo.badge && <span className="preview-badge">{promo.badge}</span>}
                    </div>
                  </td>
                  <td>
                    <div className="promo-info">
                      <strong>{promo.title}</strong>
                      {promo.subtitle && <p>{promo.subtitle}</p>}
                      {promo.link_url && <span className="link-preview">{promo.link_url}</span>}
                      {promo.cta_text && <span className="cta-preview">{promo.cta_text}</span>}
                    </div>
                  </td>
                  <td>
                    <span className={`brand-tag ${promo.brand?.toLowerCase()}`}>
                      {promo.brand || 'N/A'}
                    </span>
                  </td>
                  <td>
                    <div className="drag-handle" title="Drag to reorder">
                      <span>⋮⋮</span>
                    </div>
                  </td>
                  <td>
                    <div className="schedule-info">
                      <div>Start: {formatDate(promo.start_at)}</div>
                      <div>End: {formatDate(promo.end_at)}</div>
                    </div>
                  </td>
                  <td>
                    <button
                      className={`btn-toggle-status ${promo.is_active ? 'active' : 'inactive'}`}
                      onClick={() => handleToggleStatus(promo.id)}
                    >
                      {promo.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td>
                    <div className="promo-actions">
                      <button className="btn-edit" onClick={() => openModal(promo)}>
                        Edit
                      </button>
                      <button className="btn-delete" onClick={() => handleDelete(promo.id)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* CMS-Style Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content-cms" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-cms">
              <div>
                <h3>{editingPromo ? 'Edit Promotion' : 'Create New Promotion'}</h3>
                <p className="modal-subtitle">Configure your homepage hero campaign</p>
              </div>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>

            <form onSubmit={handleSubmit} className="promo-form-cms">
              <div className="form-layout-cms">
                {/* Left Column - Form Fields */}
                <div className="form-column-left">
                  
                  {/* Section 1: Campaign Content */}
                  <div className="form-section">
                    <h4 className="section-title">Campaign Content</h4>
                    <div className="section-divider"></div>
                    
                    <div className="form-group-cms">
                      <label>Campaign Title *</label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="e.g., New Spring Collection"
                        required
                      />
                    </div>

                    <div className="form-group-cms">
                      <label>Subtitle</label>
                      <input
                        type="text"
                        value={formData.subtitle}
                        onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                        placeholder="Short description (max 2 lines)"
                      />
                    </div>

                    <div className="form-row-cms">
                      <div className="form-group-cms">
                        <label>Badge Label</label>
                        <input
                          type="text"
                          value={formData.badge}
                          onChange={(e) => setFormData({ ...formData, badge: e.target.value })}
                          placeholder="NEW, SALE, LIMITED"
                        />
                      </div>
                      <div className="form-group-cms">
                        <label>Brand</label>
                        <select
                          value={formData.brand}
                          onChange={(e) => {
                            const selectedName = e.target.value;
                            const brand = availableBrands.find(b => b.name === selectedName);
                            setFormData(prev => ({
                              ...prev,
                              brand: selectedName,
                              // Auto-fill link_url when brand changes (only if link_url is empty or was auto-set)
                              link_url: brand ? `/brands/${brand.slug}` : prev.link_url
                            }));
                          }}
                        >
                          <option value="">Select Brand</option>
                          {availableBrands.map(b => (
                            <option key={b.id} value={b.name}>{b.name}</option>
                          ))}
                          <option value="Other">Other</option>
                        </select>
                      </div>
                    </div>

                    <div className="form-row-cms">
                      <div className="form-group-cms">
                        <label>Primary CTA</label>
                        <input
                          type="text"
                          value={formData.primary_cta}
                          onChange={(e) => setFormData({ ...formData, primary_cta: e.target.value })}
                          placeholder="Shop Now"
                        />
                      </div>
                      <div className="form-group-cms">
                        <label>Secondary CTA</label>
                        <input
                          type="text"
                          value={formData.secondary_cta}
                          onChange={(e) => setFormData({ ...formData, secondary_cta: e.target.value })}
                          placeholder="Learn More"
                        />
                      </div>
                    </div>

                    <div className="form-group-cms">
                      <label>CTA Link URL *</label>
                      <input
                        type="text"
                        value={formData.link_url}
                        onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                        placeholder="/brands/brand-slug or /products?category=gel-polish"
                        required
                      />
                      <small>Where the buttons navigate to. Auto-filled when you select a brand.</small>
                    </div>
                  </div>

                  {/* Section 2: Visual Setup */}
                  <div className="form-section">
                    <h4 className="section-title">Visual Setup</h4>
                    <div className="section-divider"></div>

                    <div className="form-group-cms">
                      <label>Product Image</label>
                      <div className="image-upload-cms">
                        {primaryPreview && (
                          <div className="image-preview-cms">
                            <img src={primaryPreview} alt="Primary preview" />
                          </div>
                        )}
                        <div className="upload-controls">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleImageUpload(file, true);
                            }}
                            disabled={uploadingPrimary}
                            className="file-input-cms"
                            id="primary-upload"
                          />
                          <label htmlFor="primary-upload" className="upload-label">
                            {uploadingPrimary ? 'Uploading...' : primaryPreview ? 'Change Image' : 'Upload Image'}
                          </label>
                          <small>Max 5MB • JPG, PNG, GIF, WebP</small>
                        </div>
                      </div>
                    </div>

                    <div className="form-group-cms">
                      <label>Background Theme</label>
                      <select
                        value={formData.background_theme}
                        onChange={(e) => setFormData({ ...formData, background_theme: e.target.value })}
                      >
                        {Object.entries(backgroundThemes).map(([key, theme]) => (
                          <option key={key} value={key}>{theme.name}</option>
                        ))}
                      </select>
                      <small>Used when no background image is set</small>
                    </div>

                    <div className="form-group-cms">
                      <label>Background Image (optional)</label>
                      <div className="image-upload-cms">
                        {bgPreview && (
                          <div className="image-preview-cms image-preview-bg">
                            <img src={bgPreview} alt="Background preview" />
                            <button
                              type="button"
                              className="btn-remove-bg"
                              onClick={() => { setFormData(prev => ({ ...prev, image_secondary: '' })); setBgPreview(''); }}
                            >
                              ✕ Remove
                            </button>
                          </div>
                        )}
                        <div className="upload-controls">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleImageUpload(file, false);
                            }}
                            disabled={uploadingBg}
                            className="file-input-cms"
                            id="bg-upload"
                          />
                          <label htmlFor="bg-upload" className="upload-label">
                            {uploadingBg ? 'Uploading...' : bgPreview ? 'Change Background' : 'Upload Background'}
                          </label>
                          <small>Full-width campaign background. Overrides theme gradient. Text overlay applied automatically.</small>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Display Settings */}
                  <div className="form-section">
                    <h4 className="section-title-small">Display Settings</h4>
                    <div className="section-divider"></div>

                    <div className="form-group-cms">
                      <label>Priority</label>
                      <input
                        type="number"
                        value={formData.priority}
                        onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                        min="0"
                      />
                      <small>Higher = shown first</small>
                    </div>

                    <div className="form-row-cms">
                      <div className="form-group-cms">
                        <label>Start Date</label>
                        <input
                          type="datetime-local"
                          value={formData.start_at}
                          onChange={(e) => setFormData({ ...formData, start_at: e.target.value })}
                        />
                      </div>
                      <div className="form-group-cms">
                        <label>End Date</label>
                        <input
                          type="datetime-local"
                          value={formData.end_at}
                          onChange={(e) => setFormData({ ...formData, end_at: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column - Live Preview */}
                <div className="form-column-right">
                  <div className="preview-section">
                    <div className="preview-header">
                      <h4 className="section-title">Live Preview</h4>
                      <span className="preview-note">Scaled replica of homepage hero</span>
                    </div>
                    <div className="section-divider"></div>
                    
                    <div 
                      className="hero-preview"
                      style={{ 
                        background: bgPreview 
                          ? `url(${bgPreview}) center/cover no-repeat` 
                          : backgroundThemes[formData.background_theme]?.gradient || backgroundThemes.default.gradient,
                        position: 'relative'
                      }}
                    >
                      {bgPreview && (
                        <div style={{
                          position: 'absolute',
                          inset: 0,
                          background: 'linear-gradient(to right, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.2) 50%, transparent 100%)',
                          borderRadius: 'inherit'
                        }} />
                      )}
                      <div className="preview-content" style={{ position: 'relative', zIndex: 1 }}>
                        {formData.badge && (
                          <div className="preview-badge-pill" style={bgPreview ? { background: 'rgba(255,255,255,0.95)', color: '#111' } : undefined}>
                            {formData.badge}
                          </div>
                        )}
                        <h2 className="preview-title" style={bgPreview ? { color: '#fff' } : undefined}>
                          {formData.title || 'Campaign Title'}
                        </h2>
                        {formData.subtitle && (
                          <p className="preview-subtitle" style={bgPreview ? { color: 'rgba(255,255,255,0.85)' } : undefined}>
                            {formData.subtitle}
                          </p>
                        )}
                        <div className="preview-ctas">
                          {formData.primary_cta && (
                            <button className="preview-btn-primary" style={bgPreview ? { background: '#fff', color: '#111' } : undefined}>
                              {formData.primary_cta}
                            </button>
                          )}
                          {formData.secondary_cta && (
                            <button className="preview-btn-secondary" style={bgPreview ? { color: '#fff', borderColor: 'rgba(255,255,255,0.5)' } : undefined}>
                              {formData.secondary_cta}
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="preview-visual" style={{ position: 'relative', zIndex: 1 }}>
                        {primaryPreview ? (
                          <img src={primaryPreview} alt="Primary" className="preview-img-primary" />
                        ) : (
                          <div className="preview-placeholder">
                            <span>Upload image to preview</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sticky Footer */}
              <div className="modal-footer-cms">
                <button type="button" className="btn-cancel-cms" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-save-cms">
                  {editingPromo ? 'Update Promotion' : 'Create Promotion'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title="Delete Promotion"
        message="Are you sure you want to delete this promotion? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      {/* Bulk Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={bulkDeleteConfirm}
        title="Delete Multiple Promotions"
        message={`Are you sure you want to delete ${selectedPromotions.length} promotion${selectedPromotions.length > 1 ? 's' : ''}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        onConfirm={confirmBulkDelete}
        onCancel={cancelBulkDelete}
      />
    </div>
  );
};

export default Promotions;
